import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Grid from '@material-ui/core/Grid';
import CardContent from '@material-ui/core/CardContent';
import Box from '@material-ui/core/Box';

import history from '../history';
import { find, create } from '../feathersWrapper';
import { PersonContext } from '../contexts/PersonContext';
import { asyncHandleChange, asyncSetState } from '../functions/InputHandlers';
import { parseQuery, buildQuery, appliedFilter } from '../functions/FilterFunctions';

import FilterDialog from '../components/FilterDialog';
import DownloadDialog from '../components/DownloadDialog';
import PageGrid from '../components/PageGrid';
import PageHeader from '../components/PageHeader';
import CardBase from '../components/CardBase';
import FinancialAccountLine2 from '../components/FinancialAccountLine2';

class NetIncomeByProperty extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      filterDialog: false,
      downloadDialog: false,
      filter: {
        startDate: null,
        endDate: null,
        entityId: null,
        startDateSelect: null,
        endDateSelect: null,
        entityIdSelect: null,
      },
      additionalCriteria: [],
    };
  }

  async componentDidMount() {
    await parseQuery(this);
    const result = await this.updateReport();

    if (!result.success) {
      const { filter } = this.state;
      const newFilter = { ...filter };
      newFilter.endDateSelect = moment();
      newFilter.startDateSelect = moment().startOf('year');

      this.setState({ filter: newFilter, filterDialog: true });
    }
  }

  updateReport = async () => {
    const { basis, organizationId } = this.context;
    const { filter } = this.state;

    if (!(filter.startDate && filter.endDate)) {
      return { success: false, message: 'Please complete all required fields' };
    }

    const reportFilter = Object.assign(buildQuery(this), {
      basis,
      reportName: 'accountJournalTotals',
      type: ['Revenue', 'Expense'],
    });

    const propertiesQuery = {
      organizationId,
      $limit: 200,
      $sort: {
        address1: 1,
        id: 1,
      },
    };

    if (reportFilter.entityId) {
      propertiesQuery.entityId = reportFilter.entityId;
    }

    const properties = await find(this, 'properties', {
      query: propertiesQuery,
    });

    const accountsObject = {
      revenues: {
        accounts: [],
      },
      expenses: {
        accounts: [],
      },
      all: {
        accounts: [],
      },
      byId: {},
    };
    // launch async calls
    // get totals and prep value array and display toggle
    const accountsPromises = [
      create(this, 'reports', reportFilter).then((result) => {
        /* eslint-disable no-param-reassign */
        result.forEach((account) => {
          account.values = [];
          account.display = false;
          account.subaccounts = [];
          if (account.type === 'Expense') {
            accountsObject.expenses.accounts.push(account);
          } else if (account.type === 'Revenue') {
            accountsObject.revenues.accounts.push(account);
          }
          accountsObject.all.accounts.push(account);
          accountsObject.byId[account.id] = account;
        });
        /* eslint-enable no-param-reassign */
        return result;
      }),
    ];

    // Set initial property object without property id (first column is always all and
    // does not require property id)
    const additionalCriteriaArray = [{}];

    // get data for each property
    properties.data.forEach((property) => {
      accountsPromises.push(
        create(this, 'reports', {
          ...reportFilter,
          propertyId: property.id,
        }),
      );

      // get id for each property in forEach loop and push it into additional criteria
      // object array
      const criteriaProperty = {
        propertyId: property.id,
      };
      additionalCriteriaArray.push(criteriaProperty);
    });

    // Pushes final object into additional criteria object with property id set to "None"
    // for final column in report (none assigned)
    additionalCriteriaArray.push({
      propertyId: 'None',
    });

    // get unassigned data
    accountsPromises.push(
      create(this, 'reports', {
        ...reportFilter,
        propertyId: null,
      }),
    );

    // resolve promises
    const accountsResults = await Promise.all(accountsPromises);

    // assign parent accounts
    accountsResults[0].forEach((account) => {
      if (account.parentAccountId) {
        accountsObject.byId[account.parentAccountId].subaccounts.push(account);
      }
    });

    // set property columns to be removable if inactive and containing no data.
    // total (1st) is not removable, none assigned (last) is
    const removeColumns = properties.data.map((property) => property.inactive);
    removeColumns.unshift(false);
    removeColumns.push(true);

    // populate values array
    for (let i = 0; i < accountsResults.length; i += 1) {
      accountsResults[i].forEach((account, index) => {
        accountsResults[0][index].values.push(account.netCredits);
        if (account.netCredits !== 0) {
          removeColumns[i] = false;
          accountsResults[0][index].display = true;
          if (account.parentAccountId) {
            accountsObject.byId[account.parentAccountId].display = true;
          }
        }
      });
    }

    const headers = properties.data.map((property) => property.address1);
    headers.unshift('Total');
    headers.push('None Assigned');

    // remove unused columns
    let spliceIndex = 1;
    for (let i = 1; i < removeColumns.length; i += 1) {
      if (removeColumns[i]) {
        for (let j = 0; j < accountsResults[0].length; j += 1) {
          accountsResults[0][j].values.splice(spliceIndex, 1);
        }
        headers.splice(spliceIndex, 1);
        additionalCriteriaArray.splice(spliceIndex, 1);
      } else {
        spliceIndex += 1;
      }
    }

    await asyncSetState(
      {
        loading: false,
        headers,
        accountsObject,
        additionalCriteria: additionalCriteriaArray,
      },
      this,
    );
    return { success: true };
  };

  sumColumnValues = (accounts) => {
    const { headers } = this.state;
    const sumArray = [];
    headers.forEach(() => {
      sumArray.push(0);
    });
    accounts.forEach((account) => {
      account.values.forEach((value, index) => {
        sumArray[index] = (parseFloat(sumArray[index]) + parseFloat(value)).toFixed(2);
      });
    });
    return sumArray;
  };

  updateFilter = async (filter) => {
    await asyncHandleChange('filter', filter, this);
    const result = await this.updateReport();
    return result;
  };

  closeFilter = () => {
    const { loading } = this.state;
    if (loading) {
      this.goToReports();
    } else {
      this.setState({ filterDialog: false });
    }
  };

  goToReports = () => {
    history.replace('/reports');
  };

  exportPdf = async () => {
    const { organizationId } = this.context;
    const { location } = this.props;
    return fetch(`${import.meta.env.VITE_FEATHERS_SOCKET}/export-pdf`, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('feathers-jwt')}`,
      },
      body: JSON.stringify({
        jwt: localStorage.getItem('feathers-jwt'),
        organizationId,
        page: `${location.pathname}${location.search}`,
      }),
    }).then(async (resp) => {
      const blob = new Blob([await resp.blob()], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'net-income.pdf';
      document.body.appendChild(a);
      a.click();
    });
  };

  exportXlsx = async () => {
    const { organizationId } = this.context;
    const { location } = this.props;
    const { filter, accountsObject, headers, exportOptions } = this.state;
    return fetch(`${import.meta.env.VITE_FEATHERS_SOCKET}/export-xlsx`, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('feathers-jwt')}`,
      },
      body: JSON.stringify({
        organizationId,
        page: `${location.pathname}`,
        filter,
        accountsObject,
        headers,
        exportOptions,
        reportName: 'Net Income By Property',
      }),
    })
      .then(async (resp) => {
        if (!resp.ok) {
          const err = new Error(`File Download Error: ${resp.statusText}`);
          err.code = resp.status;
          return new Promise((resolve) => {
            this.setState(
              () => {
                throw err;
              },
              () => resolve(),
            );
          });
        }
        return resp;
      })
      .then(async (resp) => {
        if (exportOptions.deliverySelect.id === 'email') {
          return;
        }
        const blob = new Blob([await resp.blob()], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = 'net-income-by-property.xlsx';
        document.body.appendChild(a);
        a.click();
      });
  };

  setExportOptions = async (options) => {
    await asyncHandleChange('exportOptions', options, this);
  };

  actionButtons = () => [
    { text: 'Filter', action: () => this.setState({ filterDialog: true }), class: 'filter' },
    { text: 'Export', action: () => this.setState({ downloadDialog: true }), class: 'export' },
  ];

  render() {
    const { match } = this.props;
    const { accountsObject, headers, filter, filterDialog, loading, downloadDialog, additionalCriteria } = this.state;

    return (
      <PageGrid>
        <PageHeader
          match={match}
          title="Net Income By Property"
          appliedFilter={appliedFilter(this.updateFilter, this)}
          actionButtons={this.actionButtons()}
        />
        <FilterDialog
          filter={filter}
          isOpen={filterDialog}
          closeDialog={this.closeFilter}
          updateFilter={this.updateFilter}
          dateRange
          scope={['Full Portfolio', 'Sub-Portfolio']}
          required={['dateRange', 'scope']}
        />
        <DownloadDialog
          isOpen={downloadDialog}
          // exportPdf={this.exportPdf}
          exportXlsx={this.exportXlsx}
          closeDialog={() =>
            this.setState({
              downloadDialog: false,
            })
          }
          setExportOptions={this.setExportOptions}
        />
        <Grid item xs={12} md={12}>
          <CardBase>
            <CardContent>
              {!loading && (
                <Box mx="auto" mt="50px" width="fit-content" paddingX={2}>
                  <FinancialAccountLine2 values={headers} bold />
                  <FinancialAccountLine2 label="Revenues" underline overline bold />
                  {accountsObject.revenues.accounts.map((account) => (
                    <FinancialAccountLine2
                      key={account.id}
                      accountId={account.id}
                      label={account.name}
                      values={account.values}
                      display={account.display && !account.parentAccountId}
                      subaccounts={account.subaccounts}
                      indent={1}
                      filter={filter}
                      additionalCriteria={additionalCriteria}
                    />
                  ))}
                  <FinancialAccountLine2
                    label="Total Revenues"
                    values={this.sumColumnValues(accountsObject.revenues.accounts)}
                    underline
                    overline
                    bold
                    marginBottom
                  />
                  <FinancialAccountLine2 label="Expenses" underline overline bold />
                  {accountsObject.expenses.accounts.map((account) => (
                    <FinancialAccountLine2
                      key={account.id}
                      accountId={account.id}
                      label={account.name}
                      values={account.values}
                      display={account.display && !account.parentAccountId}
                      subaccounts={account.subaccounts}
                      indent={1}
                      filter={filter}
                      additionalCriteria={additionalCriteria}
                    />
                  ))}
                  <FinancialAccountLine2
                    label="Total Expenses"
                    values={this.sumColumnValues(accountsObject.expenses.accounts)}
                    underline
                    overline
                    bold
                    marginBottom
                  />
                  <FinancialAccountLine2
                    label="Net Income"
                    values={this.sumColumnValues(accountsObject.all.accounts)}
                    underline
                    overline
                    bold
                  />
                </Box>
              )}
            </CardContent>
          </CardBase>
        </Grid>
      </PageGrid>
    );
  }
}

NetIncomeByProperty.contextType = PersonContext;

NetIncomeByProperty.propTypes = {
  match: PropTypes.objectOf(PropTypes.any).isRequired,
  location: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default NetIncomeByProperty;
